import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Plate } from "@/components/Plate";
import { formatTL, formatDuration } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { CarProfile, Coins, Warning, ClockCountdown, CashRegister, Sparkle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [kasa, setKasa] = useState(null);
  const [chart, setChart] = useState([]);
  const [active, setActive] = useState([]);
  const [expiring, setExpiring] = useState(null);
  const [lots, setLots] = useState([]);

  const load = async () => {
    try {
      const [s, k, r, a, e, l] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/kasa/today"),
        api.get("/reports/revenue?period=daily"),
        api.get("/sessions/active"),
        api.get("/plate-subscriptions/expiring").catch(() => ({ data: null })),
        api.get("/parking-lots"),
      ]);
      setSummary(s.data);
      setKasa(k.data);
      setChart(r.data.data);
      setActive(a.data.slice(0, 8));
      setExpiring(e.data);
      setLots(l.data);
    } catch { /* ignore transient network errors during background poll */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const totalExpiring = (expiring?.plate_subscriptions?.length || 0) + (expiring?.users?.length || 0);
  const capacityAlerts = lots.filter((l) => l.capacity && (l.current_occupancy / l.capacity) >= 0.90);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Kontrol Paneli</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Panel</h1>
      </div>

      {/* KASA TOPLAMI - hero (tüm zamanlar net — brüt gelir eksi giderler) */}
      <Card className="rounded-sm border-primary/40 bg-primary/5">
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Kasa Toplamı</div>
            <div className="text-5xl font-bold font-mono text-primary mt-2" data-testid="dashboard-kasa-total">
              {formatTL(kasa?.kasa_net_total)}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Bugün: {kasa?.exits_today ?? 0} çıkış · {kasa?.entries_today ?? 0} giriş
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 min-w-[300px]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-green-700">Nakit (Bugün)</div>
              <div className="text-sm font-mono font-bold text-green-700">{formatTL(kasa?.cash_in_till)}</div>
              <div className="text-[10px] text-muted-foreground">{kasa?.by_method?.cash?.count ?? 0} işlem</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-blue-700">Kart (Bugün)</div>
              <div className="text-sm font-mono font-bold text-blue-700">{formatTL(kasa?.card_collected)}</div>
              <div className="text-[10px] text-muted-foreground">{kasa?.by_method?.card?.count ?? 0} çekim</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-purple-700">EFT (Bugün)</div>
              <div className="text-sm font-mono font-bold text-purple-700">{formatTL(kasa?.bank_transfer_collected)}</div>
              <div className="text-[10px] text-muted-foreground">{kasa?.by_method?.bank_transfer?.count ?? 0} transfer</div>
            </div>
          </div>
          <Link to="/admin/kasa" className="text-xs uppercase tracking-widest font-semibold text-primary hover:underline">Detay →</Link>
        </CardContent>
      </Card>

      {/* Alerts row */}
      {(totalExpiring > 0 || capacityAlerts.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {capacityAlerts.length > 0 && (
            <Card className="rounded-sm border-destructive/40 bg-destructive/5">
              <CardContent className="p-4 flex items-center gap-3">
                <Warning size={24} weight="fill" className="text-destructive" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">Kapasite Uyarısı</div>
                  <div className="text-xs text-muted-foreground">
                    {capacityAlerts.map((l) => `${l.name} %${Math.round(l.current_occupancy / l.capacity * 100)}`).join(" · ")}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {totalExpiring > 0 && (
            <Link to="/admin/plate-subs" className="block">
              <Card className="rounded-sm border-amber-400 bg-amber-50 hover:bg-amber-100 transition">
                <CardContent className="p-4 flex items-center gap-3">
                  <Sparkle size={24} weight="fill" className="text-amber-600" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-amber-800">Abonelik Uyarısı</div>
                    <div className="text-xs text-amber-700">{totalExpiring} aboneliğin süresi yaklaşıyor veya doldu</div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Aktif Araç</div>
                <div className="text-3xl font-bold mt-2" data-testid="kpi-active-total">{summary?.active_total ?? "—"}</div>
                <div className="text-xs text-muted-foreground mt-1">{summary?.active_ust_kat ?? 0} üst / {summary?.active_alt_kat ?? 0} alt</div>
              </div>
              <CarProfile size={22} className="text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Bugün Gelir</div>
                <div className="text-3xl font-bold mt-2" data-testid="kpi-today-revenue">{formatTL(summary?.today_revenue)}</div>
                <div className="text-xs text-muted-foreground mt-1">{summary?.today_sessions ?? 0} çıkış</div>
              </div>
              <Coins size={22} className="text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Ödenmemiş</div>
                <div className={`text-3xl font-bold mt-2 ${(summary?.unpaid_count ?? 0) > 0 ? "text-destructive" : ""}`} data-testid="kpi-unpaid-count">{summary?.unpaid_count ?? "—"}</div>
                <div className="text-xs text-muted-foreground mt-1">{formatTL(summary?.unpaid_amount)}</div>
              </div>
              <Warning size={22} className="text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Ort. Süre</div>
                <div className="text-3xl font-bold mt-2">
                  {active.length ? formatDuration(Math.round(active.reduce((s, x) => s + (x.current_duration_minutes || 0), 0) / active.length)) : "—"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">aktif oturumlar</div>
              </div>
              <ClockCountdown size={22} className="text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-6">
          <h3 className="font-semibold uppercase tracking-widest text-sm mb-4">Son 7 Gün Gelir (Üst / Alt)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "4px", fontSize: "12px" }} />
                <Bar dataKey="ust_kat" fill="hsl(var(--primary))" name="Üst Kat" />
                <Bar dataKey="alt_kat" fill="hsl(var(--foreground))" name="Alt Kat" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-border">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold uppercase tracking-widest text-sm">Aktif Oturumlar</h3>
            <span className="text-xs text-muted-foreground flex items-center gap-1.5"><span className="pulse-dot" /> Canlı</span>
          </div>
          <div className="divide-y divide-border">
            {active.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Aktif oturum yok</div>}
            {active.map((s) => (
              <div key={s.id} className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Plate country={s.country_code}>{s.plate}</Plate>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}</div>
                    <div className="text-sm">{formatDuration(s.current_duration_minutes)}</div>
                  </div>
                </div>
                <div className="font-mono font-semibold text-primary text-lg">{formatTL(s.running_fee)}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
